import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import * as cognito from "./cognito";
import { config } from "../config";

type AuthState = {
  email: string | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  signOut: () => void;
  getIdToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthState | null>(null);

async function fetchIsAdmin(token: string): Promise<boolean> {
  try {
    const res = await fetch(`${config.apiUrl}/admin/me`, {
      headers: { Authorization: token },
    });
    if (!res.ok) return false;
    const json = await res.json();
    return Boolean(json?.admin);
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    cognito.currentSession().then(async (session) => {
      if (session) {
        const payload = session.getIdToken().decodePayload() as { email?: string };
        setEmail(payload.email ?? null);
        const token = session.getIdToken().getJwtToken();
        setIsAdmin(await fetchIsAdmin(token));
      }
      setLoading(false);
    });
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await cognito.signIn(email, password);
    setEmail(email);
    const session = await cognito.currentSession();
    if (session) {
      setIsAdmin(await fetchIsAdmin(session.getIdToken().getJwtToken()));
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    await cognito.signUp(email, password);
  }, []);

  const confirmSignUp = useCallback(async (email: string, code: string) => {
    await cognito.confirmSignUp(email, code);
  }, []);

  const resendCode = useCallback(async (email: string) => {
    await cognito.resendCode(email);
  }, []);

  const signOut = useCallback(() => {
    cognito.signOut();
    setEmail(null);
    setIsAdmin(false);
  }, []);

  const getIdToken = useCallback(async () => {
    const session = await cognito.currentSession();
    return session ? session.getIdToken().getJwtToken() : null;
  }, []);

  return (
    <AuthContext.Provider
      value={{ email, loading, isAdmin, signIn, signUp, confirmSignUp, resendCode, signOut, getIdToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
